import type {
  SnapClaimInviteResult,
  SnapClaimOffer,
  SnapEndpointDiscovery,
  SnapError
} from '../types';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';
import { CustomerMessages, mapCustomerError } from '../security/customerErrors';

const DEFAULT_INVITE_PATH = '/api/v1/snap-integration/claim/invite';

/**
 * PR-8.7D.5 — start an optional account claim ("Claim your support history" / "Open in NoeVault").
 *
 * Sends only the bootstrap authorization the SDK already holds, so no AppKey is involved. The claim
 * token and verification code are delivered to the requester's email out-of-band and never returned
 * here, which is what keeps a stolen bootstrap token from completing a claim on its own.
 */
export async function requestAccountClaim(args: {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  claim: SnapClaimOffer | null | undefined;
  authorization: string;
  correlationId: string;
  email?: string;
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
}): Promise<{ ok: true; result: SnapClaimInviteResult } | { ok: false; error: SnapError }> {
  if (!args.claim?.offered) {
    return {
      ok: false,
      error: createSnapError(
        'capability_denied',
        args.claim?.alreadyClaimed
          ? SnapDenyCodes.ClaimAlreadyLinked
          : SnapDenyCodes.ClaimDisabled,
        args.claim?.alreadyClaimed
          ? 'This support history is already linked to a NoeVault account.'
          : 'Claiming a NoeVault account is not available here.',
        { correlationId: args.correlationId, userSafe: true }
      )
    };
  }

  const base = args.apiBaseUrl.replace(/\/+$/, '');
  const path = args.claim.invitePath || DEFAULT_INVITE_PATH;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  try {
    const response = await args.fetchImpl(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [args.endpoints.correlationHeaderName]: args.correlationId,
        [args.endpoints.bootstrapHeaderName]: args.authorization
      },
      credentials: 'omit',
      body: JSON.stringify({
        // Only honoured when the requester has no address on file; a mismatch is rejected server-side.
        email: args.claim.requiresEmail ? args.email : undefined,
        correlationId: args.correlationId
      }),
      signal: args.signal
    });

    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      body = {};
    }

    if (!response.ok) {
      const code = String(body.code ?? body.Code ?? '');
      const message = String(body.message ?? body.Message ?? '');
      if (response.status === 401 || response.status === 403) {
        return {
          ok: false,
          error: mapCustomerError({
            code,
            message,
            httpStatus: response.status,
            correlationId: args.correlationId
          })
        };
      }
      return {
        ok: false,
        error: createSnapError(
          'transport_failed',
          code || SnapDenyCodes.TransportFailed,
          message || CustomerMessages.submissionFailed,
          { correlationId: args.correlationId, retryable: response.status >= 500, userSafe: true }
        )
      };
    }

    return {
      ok: true,
      result: {
        success: true,
        correlationId: String(body.correlationId ?? body.CorrelationId ?? args.correlationId),
        code: (body.code ?? body.Code ?? null) as string | null,
        message: String(body.message ?? body.Message ?? ''),
        maskedEmail: (body.maskedEmail ?? body.MaskedEmail ?? null) as string | null,
        expiresAtUtc: (body.expiresAtUtc ?? body.ExpiresAtUtc ?? null) as string | null
      }
    };
  } catch {
    return {
      ok: false,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        CustomerMessages.submissionFailed,
        { correlationId: args.correlationId, retryable: true, userSafe: true }
      )
    };
  }
}
